<script setup lang="ts">
import { createFinancials } from '@/helpers/builder';
import { EmploymentType } from '@/helpers/const';
import { shouldValidateIncomeForm, validateIncomeForms } from '@/helpers/incomeFormValidation';
import { useDeclarationProcess } from '@/helpers/useDeclarationProcess';
import { useApplicantPermissions } from '@/helpers/useApplicantPermissions';
import { DeclarationProcessStep, type ApplicantFinancials, type ApplicantId } from '@/types';
import { useTemplateRefsList } from '@vueuse/core';
import { cloneDeep } from 'lodash-es';
import IncomeForm from './modules/Income/IncomeForm.vue';
import ErhlIncomeForm from '@/views/erhl-customer-application/modules/Income/IncomeForm.vue';
import { computed, nextTick, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { getFullName } from '@/helpers/format';
import { CheckIcon, XMarkIcon } from '@heroicons/vue/20/solid';

const { authorisedUser, loanApp, loanApplicants, saveAndGoNext, goBack, send, state } = useDeclarationProcess();
const route = useRoute();
const router = useRouter();
const { getVisibleApplicantsForIncome, canEditApplicantIncome, getApplicantIncomeStatus } = useApplicantPermissions();

const error = ref('');

// Use permissions composable for visible applicants
const validApplicants = getVisibleApplicantsForIncome;

const initFinancials = validApplicants.value.reduce((obj, item) => {
  obj[item] = createFinancials();
  return obj;
}, {} as ApplicantFinancials);

const financials = ref({ ...initFinancials, ...cloneDeep(loanApp.value.financials) });
const isErhl = computed(() => loanApp.value.lendingProduct === 'erhl');
const incomeRefs = useTemplateRefsList<InstanceType<typeof IncomeForm>>();
const erhlIncomeRefs = useTemplateRefsList<InstanceType<typeof ErhlIncomeForm>>();
const activeTab = ref<ApplicantId | undefined>(
  validApplicants.value.length === 1 && canEditApplicantIncome(validApplicants.value[0])
    ? validApplicants.value[0]
    : undefined,
);
const openedApplicantIds = new Set<ApplicantId>(activeTab.value ? [activeTab.value] : []);
const invalidEmploymentId = ref();

const openIncomeForm = (applicantId: ApplicantId) => {
  openedApplicantIds.add(applicantId);
  activeTab.value = applicantId;
};

const cancelIncomeForm = (applicantId: ApplicantId) => {
  openedApplicantIds.delete(applicantId);
  activeTab.value = undefined;
};

const validateIncome = async () => {
  const editableApplicants = validApplicants.value.filter((id) => canEditApplicantIncome(id));

  if (editableApplicants.length === 0 || incomeRefs.value.length === 0) {
    return true;
  }

  const validation = await validateIncomeForms({
    applicantIds: editableApplicants,
    forms: incomeRefs.value,
    shouldValidate: (applicantId) =>
      shouldValidateIncomeForm(openedApplicantIds.has(applicantId), financials.value[applicantId]?.income ?? null),
  });

  if (!validation.validated) {
    openIncomeForm(validation.applicantId);
    invalidEmploymentId.value = validation.employmentId;
    error.value = `${getFullName(
      loanApplicants.value[validation.applicantId],
    )}'s income details are incomplete. Please review the highlighted fields.`;
    await nextTick();
    validation.form.scrollToFirstError();
    return false;
  }

  return true;
};

/**
 * Saves the Equity Release income form, including retirement details, for the signed-in applicant.
 */
const declarationStep = () => {
  const value = state.value.value;
  return typeof value === 'string' ? value : '';
};

const saveErhlIncome = async () => {
  const editableApplicants = validApplicants.value.filter((id) => canEditApplicantIncome(id));
  await nextTick();
  const forms = [...erhlIncomeRefs.value];
  if (forms.length === 0) {
    error.value = 'Income form is not ready. Refresh and try again.';
    return;
  }
  try {
    for (let index = 0; index < forms.length; index++) {
      const isValid = await forms[index].update();
      if (!isValid) {
        const applicantId = editableApplicants[index];
        if (applicantId) openIncomeForm(applicantId);
        const applicant = applicantId ? loanApplicants.value[applicantId] : undefined;
        error.value = applicant
          ? `${getFullName(applicant)}'s income details are incomplete. Please review the highlighted fields.`
          : 'Income details are incomplete. Please review the highlighted fields.';
        return;
      }
    }
  } catch (caught) {
    const message = caught instanceof Error ? caught.message : 'Income details could not be saved.';
    error.value = message;
    return;
  }
  error.value = '';
  if (declarationStep() !== DeclarationProcessStep.Income) {
    send({ type: 'GOTO', step: DeclarationProcessStep.Income });
  }
  saveAndGoNext({ financials: financials.value });
  await nextTick();
  if (route.name === 'declarationExpenses') return;
  try {
    await router.push({ name: 'declarationExpenses' });
  } catch {
    if (route.name !== 'declarationExpenses') {
      error.value = 'Income could not be continued. Please review the highlighted fields and try again.';
    }
  }
};

onMounted(() => {
  if (isErhl.value && declarationStep() !== DeclarationProcessStep.Income) {
    send({ type: 'GOTO', step: DeclarationProcessStep.Income });
  }
});

const saveAndContinue = async () => {
  if (isErhl.value) {
    await saveErhlIncome();
    return;
  }
  if (await validateIncome()) {
    const editableApplicants = validApplicants.value.filter((id) => canEditApplicantIncome(id));
    const requiredIncomeApplicantId =
      loanApp.value.userType === 'Broker'
        ? editableApplicants[0]
        : editableApplicants.find((id) => id === authorisedUser.id);
    if (requiredIncomeApplicantId && !financials.value[requiredIncomeApplicantId]?.income?.employment.length) {
      error.value = 'Income source is required.';
      openIncomeForm(requiredIncomeApplicantId);
      await nextTick();
      incomeRefs.value[editableApplicants.indexOf(requiredIncomeApplicantId)]?.scrollToFirstError();
      return;
    }
    error.value = '';
    return saveAndGoNext({
      financials: financials.value,
    });
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const cardTitle = (employment: any) => {
  let description = '';
  if (employment.type === EmploymentType.SELF_EMPLOYED) {
    description = employment.tradingName;
  } else if (
    employment.type === EmploymentType.FULL_TIME ||
    employment.type === EmploymentType.PART_TIME ||
    employment.type === EmploymentType.CASUAL
  ) {
    description = employment.employerName;
  }
  return `${employment.type.charAt(0).toUpperCase() + employment.type.slice(1).replace('_', ' ')}: ${description}`;
};

const cardDescription = (applicantId: ApplicantId) => {
  return financials.value[applicantId]?.income?.employment?.map((item) => {
    return cardTitle(item);
  });
};

const hasIncomeData = (applicantId: ApplicantId) => {
  return (financials.value[applicantId]?.income?.employment?.length ?? 0) > 0;
};
</script>

<template>
  <div class="w-full">
    <div class="mb-4 pb-2">
      <h2 class="text-2xl font-medium">
        What are your
        <BxHighlight>income</BxHighlight>
        sources?
      </h2>
    </div>

    <div class="my-6 grid gap-4">
      <div v-for="(applicantId, i) in validApplicants" :key="applicantId">
        <!-- Expanded Form View (only for editable applicants) -->
        <BxCard v-if="canEditApplicantIncome(applicantId)" :class="applicantId !== activeTab && 'hidden'">
          <h2 class="mb-4 text-xl font-medium">
            {{ getFullName(loanApplicants[applicantId]) }}
          </h2>
          <ErhlIncomeForm
            v-if="isErhl"
            :ref="erhlIncomeRefs.set"
            v-model="financials[applicantId].income"
            :applicant-id="applicantId"
            :retirement-details="financials[applicantId]?.retirementDetails"
            :action-type="loanApp.actionType"
            :animate="applicantId === activeTab"
            :show-buttons="validApplicants.length > 1"
            @update:retirement-details="(details) => (financials[applicantId].retirementDetails = details)"
            @save="activeTab = undefined"
            @cancel="cancelIncomeForm(applicantId)"
          />
          <IncomeForm
            v-else
            :ref="incomeRefs.set"
            v-model="financials[applicantId].income"
            :applicant-id="applicantId"
            :animate="applicantId === activeTab"
            :show-buttons="validApplicants.length > 1"
            :is-secondary="i > 0"
            :applicants="validApplicants"
            :invalid-employment-id="invalidEmploymentId"
            :disabled="!canEditApplicantIncome(applicantId)"
            @save="activeTab = undefined"
            @cancel="cancelIncomeForm(applicantId)"
          />
        </BxCard>

        <!-- Collapsed Card View (Desktop) -->
        <BxCard :class="`${applicantId === activeTab && 'hidden'} employment-card`">
          <div class="flex w-full items-center gap-6">
            <div class="card-details">
              <div class="mb-2 font-medium">
                {{ getFullName(loanApplicants[applicantId]) }} :
                <span class="text-gray-400">
                  {{ getApplicantIncomeStatus(applicantId).isCompleted ? 'Completed' : 'Not Completed' }}
                </span>
              </div>
              <div class="flex items-center gap-2">
                <div class="h-5 w-5">
                  <CheckIcon v-if="hasIncomeData(applicantId)" class="text-green-600" />
                  <XMarkIcon v-else class="text-rose-600" />
                </div>
                Employment details
              </div>
              <div class="flex items-center gap-2">
                <div class="h-5 w-5">
                  <CheckIcon
                    v-if="financials[applicantId]?.income?.expectIncomeChange !== undefined"
                    class="text-green-600"
                  />
                  <XMarkIcon v-else class="text-rose-600" />
                </div>
                Additional income
              </div>
            </div>
            <div class="employment-descriptions flex-1">
              <div>
                <div v-for="description in cardDescription(applicantId)" :key="description">
                  <span class="text-gray-400">{{ description }}</span>
                </div>
              </div>
            </div>
            <div v-if="canEditApplicantIncome(applicantId)" class="ml-auto shrink-0">
              <BxButton
                :id="applicantId + '-add-income'"
                variant="secondary"
                :disabled="activeTab && activeTab !== applicantId"
                @click="openIncomeForm(applicantId)"
              >
                {{ hasIncomeData(applicantId) ? 'Edit' : 'Add' }} income
              </BxButton>
            </div>
          </div>
        </BxCard>

        <!-- Collapsed Card View (Mobile) -->
        <BxCard :class="`${applicantId === activeTab && 'hidden'} employment-card-mobile`">
          <div class="items-left flex flex-col">
            <div class="card-details">
              <div class="mb-2 font-medium">
                {{ getFullName(loanApplicants[applicantId]) }} :
                <span class="text-gray-400">
                  {{ getApplicantIncomeStatus(applicantId).isCompleted ? 'Completed' : 'Not Completed' }}
                </span>
              </div>
              <div class="items-left flex gap-2">
                <div class="h-5 w-5">
                  <CheckIcon v-if="hasIncomeData(applicantId)" class="text-green-600" />
                  <XMarkIcon v-else class="text-rose-600" />
                </div>
                Employment details
              </div>
              <div class="items-left flex gap-2">
                <div class="h-5 w-5">
                  <CheckIcon
                    v-if="financials[applicantId]?.income?.expectIncomeChange !== undefined"
                    class="text-green-600"
                  />
                  <XMarkIcon v-else class="text-rose-600" />
                </div>
                Additional income
              </div>
            </div>
            <div class="employment-descriptions ml-6">
              <div>
                <div v-for="description in cardDescription(applicantId)" :key="description">
                  <span class="text-gray-400">{{ description }}</span>
                </div>
              </div>
            </div>
          </div>
          <div v-if="canEditApplicantIncome(applicantId)" class="card-button">
            <BxButton
              variant="secondary"
              :disabled="activeTab && activeTab !== applicantId"
              @click="openIncomeForm(applicantId)"
            >
              {{ hasIncomeData(applicantId) ? 'Edit' : 'Add' }} income
            </BxButton>
          </div>
        </BxCard>
      </div>
      <span v-if="error" class="mt-6 block text-sm text-rose-500">
        {{ error }}
      </span>
    </div>

    <div class="mt-6">
      <BxButton id="income-continue" variant="primary" @click="saveAndContinue()">Save and continue</BxButton>
      <BxButton variant="link" class="ml-4" @click="goBack()">Back</BxButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.employment-descriptions {
  @media (max-width: 767px) {
    margin-top: 20px;
    margin-bottom: 20px;
  }
}
.employment-card {
  @media (max-width: 767px) {
    display: none;
  }
}

.employment-card-mobile {
  @media (min-width: 767px) {
    display: none;
  }
}

.card-button {
  flex-grow: 1;
  @media (min-width: 767px) {
    text-align: right;
  }
  @media (max-width: 767px) {
    text-align: center;
  }
  flex-basis: 200px;
}
</style>
